import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  LuMail,
  LuCalendar,
  LuKeyRound,
  LuLandmark,
  LuLogOut,
  LuTrash2,
  LuBellRing,
  LuEye,
  LuLifeBuoy,
} from 'react-icons/lu';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import PageHeader from '../components/oracle/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import MembershipCard from '../components/billing/MembershipCard';
import SupportModal from '../components/support/SupportModal';
import { SUPPORT_ERRORS } from '../contracts/supportApi';
import { formatMemberSince } from '../lib/membershipDate';
import { doSignOut, doDeleteAccount } from '../firebase/Auth';
import { ROUTES } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';
import logoFace from '../assets/logo-face.png';

// Account surface (design-recreation). One responsive DOM tree:
// desktop (≥lg) renders full-width cards; below lg the same content
// collapses into the centered phone layout.
//
//   delete flow:  IDLE ──arm──▶ ARMED ──confirm──▶ (deleting…) ──ok──▶ /sign-in
//                   ▲             │                    │
//                   └──cancel─────┘                    └─fail─▶ ARMED + error
//
// Email-password users must re-authenticate, so the ARMED state includes a
// current-password field for them; Google users skip it.

const deleteErrorMessage = (err) => {
  const code = err && err.code;
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    return 'Incorrect password. Your archive remains intact.';
  }
  // Server-side proof of presence. Admin-SDK deletion needs no recent login,
  // so this is enforced by deleteAccount via auth_time rather than by the
  // client — which is why Google users now hit it too, having previously had
  // no gate at all on an irreversible action.
  if (code === 'REAUTH_REQUIRED' || code === 'auth/requires-recent-login') {
    return 'Recent sign-in required. Sign out, sign in again, then retry.';
  }
  // The fail-closed billing gate. Nothing was deleted, and saying so is the
  // whole point: a member who reads "deletion failed" without that assurance
  // assumes their archive is half-gone.
  if (code === 'BILLING_CANCEL_FAILED') {
    return 'Your membership could not be cancelled, so nothing was deleted. Try again shortly.';
  }
  return (err && err.message) || 'Deletion failed. Your archive remains intact.';
};

// Who the account belongs to. The header said "Anonymous Member" to everyone,
// including people who picked a username in onboarding — their own account
// page was the one surface that would not say their name back to them.
//
// Onboarding makes a username mandatory, so the fallbacks are near-unreachable
// and exist for pre-onboarding and legacy docs. NOT derived from the email:
// the address is already one row down under its own label, and inventing a
// name out of it is a guess wearing the costume of a fact.
const accountName = (userData) =>
  (userData && (userData.username || userData.display_name)) || 'Member';

const Account = () => {
  const navigate = useNavigate();
  const { currentUser, isGoogleUser, isEmailUser } = useAuth();
  const { userData, updateUserData } = useUser() || {};

  const [scanCount, setScanCount] = useState(null);
  const [emailPrefBusy, setEmailPrefBusy] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingError, setSharingError] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportBusy, setSupportBusy] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [supportSent, setSupportSent] = useState(false);

  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = httpsCallable(functions, 'listVaultEntries');
        const result = await list({});
        const data = result && result.data;
        if (!cancelled && data && data.status === 'success' && Array.isArray(data.entries)) {
          setScanCount(
            data.next_cursor ? `${data.entries.length}+` : String(data.entries.length),
          );
        }
      } catch {
        // best-effort — row keeps showing the em dash
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const email = (currentUser && currentUser.email) || '—';
  const creationTime = currentUser && currentUser.metadata && currentUser.metadata.creationTime;
  const memberSinceShort = formatMemberSince(creationTime) || '—';
  const signInMethod = isGoogleUser ? 'GOOGLE' : isEmailUser ? 'EMAIL & PASSWORD' : '—';

  const detailRows = [
    { label: 'EMAIL', value: email, Icon: LuMail },
    { label: 'MEMBER SINCE', value: memberSinceShort, Icon: LuCalendar },
    { label: 'SIGN-IN METHOD', value: signInMethod, Icon: LuKeyRound },
    { label: 'SCANS ARCHIVED', value: scanCount === null ? '—' : scanCount, Icon: LuLandmark },
  ];


  // Lifecycle-email opt-out — the same users/{uid}.email_optout flag the
  // one-click unsubscribe link flips, so the toggle and the email footer stay
  // in sync (userData streams live via onSnapshot). Auth mail (confirmation,
  // password reset) is transactional and unaffected.
  const emailOptout = Boolean(userData && userData.email_optout);
  const toggleEmailOptout = async () => {
    if (emailPrefBusy || !userData || !updateUserData) return;
    setEmailPrefBusy(true);
    try {
      await updateUserData({ email_optout: !emailOptout });
    } catch (err) {
      console.error('Email preference update failed:', err);
    } finally {
      setEmailPrefBusy(false);
    }
  };

  // Portrait sharing — whether other members can read this user's scans in the
  // judging deck (anonymized, bar across the eyes, no handle attached).
  //
  // Goes through a CALLABLE rather than updateUserData(), unlike the email
  // toggle above. Two reasons: `feedback_default` is deliberately not on the
  // firestore.rules users hasOnly() allow-list (a field missing from that list
  // fails permission-denied *silently* — the PR #76 bug), and the preference
  // has to fan out onto every scan doc, which only the server can do.
  //
  // The toggle shows the EFFECTIVE state, mirroring the server's
  // resolveJudgable (functions/utilities/scanSharing.js): an explicit boolean
  // on the user doc wins; otherwise the environment's sharing default applies.
  // REACT_APP_SCAN_SHARING_DEFAULT_ON is written by the same deploy step that
  // bakes SCAN_SHARING_DEFAULT_ON into functions/.env, so the two halves move
  // together per environment. Reading `feedback_default === true` alone showed
  // never-asked users OFF while their scans defaulted into the deck — the one
  // consent surface reporting the opposite of reality.
  const sharingDefaultOn =
    process.env.REACT_APP_SCAN_SHARING_DEFAULT_ON === 'true';
  const sharingEnabled =
    typeof userData?.feedback_default === 'boolean'
      ? userData.feedback_default
      : sharingDefaultOn;
  const toggleSharing = async () => {
    if (sharingBusy || !userData) return;
    setSharingBusy(true);
    setSharingError(null);
    try {
      const setScanSharing = httpsCallable(functions, 'setScanSharing');
      const res = await setScanSharing({ enabled: !sharingEnabled });
      if (res?.data?.status !== 'success') {
        setSharingError(res?.data?.error_message || COPY.SHARING_ERROR);
      }
      // On success the live userData snapshot flips the label — no local state.
    } catch (err) {
      console.error('Sharing preference update failed:', err);
      setSharingError(COPY.SHARING_ERROR);
    } finally {
      setSharingBusy(false);
    }
  };

  const handleSignOut = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await doSignOut();
    } finally {
      setBusy(false);
      navigate(ROUTES.SIGN_IN);
    }
  };

  // Support. The error path deliberately leaves the modal open and the typed
  // message intact — see the note in SupportModal. Server error codes map to
  // their own copy so "you have written three times this hour" does not read as
  // "something broke", which would invite a fourth attempt.
  const openSupport = () => {
    setSupportError(null);
    setSupportSent(false);
    setSupportOpen(true);
  };

  const closeSupport = () => {
    if (supportBusy) return;
    setSupportOpen(false);
  };

  const sendSupport = async ({ topic, message }) => {
    if (supportBusy) return;
    setSupportBusy(true);
    setSupportError(null);
    try {
      const send = httpsCallable(functions, 'sendSupportMessage');
      const res = await send({ topic, message });
      if (res?.data?.status === 'success') {
        setSupportSent(true);
        return;
      }
      const code = res?.data?.error_code;
      if (code === SUPPORT_ERRORS.RATE_LIMITED) setSupportError(COPY.SUPPORT_ERROR_RATE_LIMITED);
      else if (code === SUPPORT_ERRORS.NO_EMAIL) setSupportError(COPY.SUPPORT_ERROR_NO_EMAIL);
      else setSupportError(COPY.SUPPORT_ERROR_GENERIC);
    } catch (err) {
      console.error('Support message failed:', err);
      setSupportError(COPY.SUPPORT_ERROR_GENERIC);
    } finally {
      setSupportBusy(false);
    }
  };

  const armDelete = () => {
    setDeleteArmed(true);
    setDeleteError('');
    setDeletePassword('');
  };

  const cancelDelete = () => {
    setDeleteArmed(false);
    setDeleteError('');
    setDeletePassword('');
  };

  const confirmDelete = async () => {
    if (busy) return;
    setBusy(true);
    setDeleteError('');
    try {
      await doDeleteAccount(isEmailUser ? deletePassword : null);
      navigate(ROUTES.SIGN_IN);
    } catch (err) {
      setDeleteError(deleteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout
      header={(
        <PageHeader
          title="ACCOUNT"
          subtitle="Manage your identity, privacy, and session."
          mobileSubtitle={null}
        />
      )}
    >
      <div className="px-5 lg:px-12 pb-10 lg:pb-14 font-display text-bone">
        <div className="mt-2 lg:mt-2 flex flex-col gap-6 lg:gap-5">
          {/* profile */}
          <div className="flex flex-col items-center text-center gap-0 lg:flex-row lg:items-center lg:text-left lg:gap-[22px] lg:bg-[#1B1B1A] lg:border lg:border-hairline-soft lg:rounded-card lg:px-[26px] lg:py-6">
            <div className="w-[84px] h-[84px] lg:w-[72px] lg:h-[72px] rounded-full bg-[#232322] border border-[rgba(201,164,92,0.4)] flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={logoFace}
                alt=""
                className="w-[60px] h-[60px] lg:w-[52px] lg:h-[52px] object-contain mix-blend-screen"
              />
            </div>
            <div className="flex-1 min-w-0 mt-3.5 lg:mt-0">
              <div className="text-[21px] lg:text-[24px] text-bone truncate" data-testid="account-name">
                {accountName(userData)}
              </div>
            </div>
          </div>

          {/* Membership. Its own block rather than a chip tucked into the
              profile row: the profile answers "who is this account", the card
              answers "what is this account paying for and how do I change it".
              Those are different questions and the second one is the one
              people come to this page for. */}
          <MembershipCard />

          {/* details */}
          <div className="lg:bg-[#161615] lg:border lg:border-hairline-soft lg:rounded-card px-1 lg:px-6 lg:py-2">
            {detailRows.map(({ label, value, Icon }) => (
              <div
                key={label}
                className="flex items-center justify-between py-3.5 lg:py-[17px] border-b border-hairline-soft last:border-b-0"
              >
                <span className="flex items-center gap-3 lg:gap-3.5">
                  <Icon size={16} className="text-bone-dim flex-shrink-0" aria-hidden="true" />
                  <span className="text-[11px] lg:text-xs tracking-[0.18em] text-bone-dim">
                    {label}
                  </span>
                </span>
                <span className="text-[15px] lg:text-[17px] text-bone truncate pl-3">{value}</span>
              </div>
            ))}
          </div>

          {/* email preferences */}
          <div className="lg:bg-[#161615] lg:border lg:border-hairline-soft lg:rounded-card px-1 lg:px-6 lg:py-2">
            <div className="flex items-center justify-between gap-4 py-3.5 lg:py-[17px]">
              <span className="flex items-center gap-3 lg:gap-3.5 min-w-0">
                <LuBellRing size={16} className="text-bone-dim flex-shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-[11px] lg:text-xs tracking-[0.18em] text-bone-dim">
                    CHECK-INS &amp; OFFERS
                  </span>
                  <span className="block text-[13px] lg:text-sm italic text-bone-dim mt-1">
                    Scan reminders and member mail. Account and security notices are unaffected.
                  </span>
                </span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={!emailOptout}
                aria-label="CHECK-INS AND OFFERS"
                onClick={toggleEmailOptout}
                disabled={emailPrefBusy || !userData}
                className={[
                  'flex-shrink-0 bg-transparent rounded-full px-4 py-2 lg:px-5 lg:py-2.5 cursor-pointer',
                  'font-display uppercase text-[10px] lg:text-[11px] tracking-[0.2em]',
                  'transition-colors duration-200 ease-mogged disabled:opacity-50 disabled:cursor-default',
                  emailOptout
                    ? 'border border-[rgba(255,255,255,0.22)] text-bone-dim hover:border-[rgba(255,255,255,0.5)] hover:text-bone'
                    : 'border border-[rgba(201,164,92,0.5)] text-aurum-bright hover:border-aurum-bright',
                ].join(' ')}
              >
                {emailOptout ? 'UNSUBSCRIBED' : 'SUBSCRIBED'}
              </button>
            </div>
          </div>

          {/* portrait sharing */}
          <div className="lg:bg-[#161615] lg:border lg:border-hairline-soft lg:rounded-card px-1 lg:px-6 lg:py-2">
            <div className="flex items-center justify-between gap-4 py-3.5 lg:py-[17px]">
              <span className="flex items-center gap-3 lg:gap-3.5 min-w-0">
                <LuEye size={16} className="text-bone-dim flex-shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-[11px] lg:text-xs tracking-[0.18em] text-bone-dim">
                    {COPY.SHARING_LABEL}
                  </span>
                  <span className="block text-[13px] lg:text-sm italic text-bone-dim mt-1">
                    {COPY.SHARING_BODY}
                  </span>
                </span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={sharingEnabled}
                aria-label={COPY.SHARING_LABEL}
                onClick={toggleSharing}
                disabled={sharingBusy || !userData}
                className={[
                  'flex-shrink-0 bg-transparent rounded-full px-4 py-2 lg:px-5 lg:py-2.5 cursor-pointer',
                  'font-display uppercase text-[10px] lg:text-[11px] tracking-[0.2em]',
                  'transition-colors duration-200 ease-mogged disabled:opacity-50 disabled:cursor-default',
                  sharingEnabled
                    ? 'border border-[rgba(201,164,92,0.5)] text-aurum-bright hover:border-aurum-bright'
                    : 'border border-[rgba(255,255,255,0.22)] text-bone-dim hover:border-[rgba(255,255,255,0.5)] hover:text-bone',
                ].join(' ')}
              >
                {sharingEnabled ? COPY.SHARING_ON : COPY.SHARING_OFF}
              </button>
            </div>
            {/* Bone, never red — DESIGN.md forbids red even for errors. */}
            {sharingError && (
              <p className="font-display text-bone text-[13px] pb-3" role="alert">
                {sharingError}
              </p>
            )}
          </div>

          {/* danger zone */}
          <div className="bg-[rgba(28,20,18,0.6)] border border-[rgba(201,90,70,0.28)] rounded-card p-4 lg:px-[26px] lg:py-6">
            <div className="text-xs tracking-[0.22em] text-[#C97A66]">SESSION &amp; DATA</div>
            <div className="flex flex-col gap-3 lg:flex-row lg:gap-3.5 mt-4 lg:mt-[18px]">
              <button
                type="button"
                onClick={handleSignOut}
                disabled={busy}
                className="flex items-center justify-center gap-2.5 w-full lg:w-auto lg:px-[26px] h-[50px] lg:h-[52px] bg-transparent border border-[rgba(255,255,255,0.22)] rounded-field text-[#E8E2D6] font-display text-xs tracking-[0.22em] transition-colors duration-200 ease-mogged hover:border-[rgba(255,255,255,0.5)] hover:text-bone disabled:opacity-50 disabled:cursor-default cursor-pointer"
              >
                <LuLogOut size={16} aria-hidden="true" />
                <span>SIGN OUT</span>
              </button>

              {!deleteArmed && (
                <button
                  type="button"
                  onClick={armDelete}
                  disabled={busy}
                  className="flex items-center justify-center gap-2.5 w-full lg:w-auto lg:px-[26px] h-[50px] lg:h-[52px] bg-transparent border border-[rgba(201,90,70,0.45)] rounded-field text-[#DD8888] font-display text-xs tracking-[0.22em] transition-colors duration-200 ease-mogged hover:border-[#C45846] hover:text-[#E89B8B] disabled:opacity-50 disabled:cursor-default cursor-pointer"
                >
                  <LuTrash2 size={16} aria-hidden="true" />
                  <span>DELETE ACCOUNT</span>
                </button>
              )}

              {deleteArmed && (
                <div className="flex-1 border border-[rgba(201,90,70,0.4)] rounded-field p-3.5 text-center lg:border-0 lg:p-0 lg:text-right">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end lg:flex-wrap">
                    <span className="text-[13px] lg:text-sm italic text-[#CC9999] leading-snug">
                      This permanently erases your archive. This cannot be undone.
                    </span>
                    {isEmailUser && (
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Current password"
                        aria-label="Current password"
                        autoComplete="current-password"
                        className="w-full lg:w-[210px] h-[46px] px-4 bg-[#1B1B1A] border border-hairline rounded-field text-[15px] text-bone placeholder:text-bone-dim placeholder:italic focus:outline-none focus:border-[rgba(255,255,255,0.4)]"
                      />
                    )}
                    <div className="flex gap-2.5 lg:gap-3">
                      <button
                        type="button"
                        onClick={cancelDelete}
                        disabled={busy}
                        className="flex-1 lg:flex-none lg:px-5 h-[46px] lg:h-[52px] bg-transparent border border-[rgba(255,255,255,0.18)] rounded-[10px] lg:rounded-field text-bone-dim font-display text-[11px] lg:text-xs tracking-[0.2em] transition-colors duration-200 ease-mogged hover:text-[#E8E2D6] disabled:opacity-50 disabled:cursor-default cursor-pointer"
                      >
                        CANCEL
                      </button>
                      <button
                        type="button"
                        onClick={confirmDelete}
                        disabled={busy || (isEmailUser && !deletePassword)}
                        className="flex-1 lg:flex-none lg:px-[22px] h-[46px] lg:h-[52px] bg-[#B04A3A] border-0 rounded-[10px] lg:rounded-field text-white font-display text-[11px] lg:text-xs font-semibold tracking-[0.2em] transition-colors duration-200 ease-mogged hover:bg-[#C45846] disabled:opacity-50 disabled:cursor-default cursor-pointer"
                      >
                        YES, DELETE
                      </button>
                    </div>
                  </div>
                  {deleteError && (
                    <div className="mt-3 text-[13px] lg:text-sm italic text-[#E89B8B]">
                      {deleteError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* support — last thing on the page, deliberately. Someone scrolling
              to the bottom of Account is either leaving or stuck, and the exits
              (sign out, delete) are directly above. This is the third door. */}
          <button
            type="button"
            onClick={openSupport}
            className="flex items-center justify-center gap-2.5 w-full lg:w-auto lg:self-start lg:px-[26px] h-[50px] lg:h-[52px] bg-transparent border border-[rgba(255,255,255,0.18)] rounded-field text-bone-dim font-display text-xs tracking-[0.22em] transition-colors duration-200 ease-mogged hover:border-[rgba(255,255,255,0.4)] hover:text-bone cursor-pointer"
            data-testid="support-open"
          >
            <LuLifeBuoy size={16} aria-hidden="true" />
            <span>{COPY.SUPPORT_BUTTON}</span>
          </button>
        </div>
      </div>

      <SupportModal
        open={supportOpen}
        onSend={sendSupport}
        onDismiss={closeSupport}
        submitting={supportBusy}
        error={supportError}
        sent={supportSent}
      />
    </AppLayout>
  );
};

export default Account;
